import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { api } from '../services/api';

interface Permission {
  id: number;
  nom_permission: string;
  description: string;
  module: string;
  action: string;
}

interface Group {
  id: number;
  nom_groupe: string;
  description: string;
  color: string;
  assigned_at: string;
  permissions: Permission[];
}

interface PermissionContextType {
  userGroups: Group[];
  userPermissions: Permission[];
  isLoading: boolean;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
  refreshPermissions: () => Promise<void>;
}

const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};

interface PermissionProviderProps {
  children: ReactNode;
}

export const PermissionProvider: React.FC<PermissionProviderProps> = ({ children }) => {
  const [userGroups, setUserGroups] = useState<Group[]>([]);
  const [userPermissions, setUserPermissions] = useState<Permission[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  // SEC-H1 : plus de `token` exposé par AuthContext (il vit dans un cookie httpOnly).
  // `isAuthenticated` porte désormais seul le signal de session active.
  const { user, isAuthenticated } = useAuth();

  const fetchUserPermissions = async () => {
    if (!user || !isAuthenticated) {
      setUserGroups([]);
      setUserPermissions([]);
      setIsLoading(false);
      return;
    }

    // BUG-H8 : les endpoints groups/permissions sont réservés à l'admin (403 sinon).
    // Inutile de les appeler pour un utilisateur normal -> évite des requêtes en échec.
    if (user.identifiant !== 'admin') {
      setUserGroups([]);
      setUserPermissions([]);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);

      // Via l'instance axios partagée (cookie d'auth + gestion 401 centralisés).
      // allSettled : un échec sur l'un n'empêche pas l'autre.
      const [groupsRes, permsRes] = await Promise.allSettled([
        api.get(`/api/admin/users/${user.id}/groups`),
        api.get(`/api/admin/users/${user.id}/permissions`),
      ]);

      if (groupsRes.status === 'fulfilled' && groupsRes.value.data?.success) {
        setUserGroups(groupsRes.value.data.groups || []);
      } else {
        setUserGroups([]);
      }

      if (permsRes.status === 'fulfilled' && permsRes.value.data?.success) {
        setUserPermissions(permsRes.value.data.permissions || []);
      } else {
        setUserPermissions([]);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des permissions:', error);
      setUserGroups([]);
      setUserPermissions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const hasPermission = (permission: string): boolean => {
    return userPermissions.some(p => p.nom_permission === permission);
  };

  const hasAnyPermission = (permissions: string[]): boolean => {
    return permissions.some(permission => hasPermission(permission));
  };

  const hasAllPermissions = (permissions: string[]): boolean => {
    return permissions.every(permission => hasPermission(permission));
  };

  const refreshPermissions = async () => {
    await fetchUserPermissions();
  };

  // BUG-M3 : dépendre de `user` (identité d'objet, recréé à chaque setUser) relançait
  // le fetch sans changement réel. On dépend des valeurs qui comptent vraiment.
  useEffect(() => {
    fetchUserPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.identifiant, isAuthenticated]);

  const value = {
    userGroups,
    userPermissions,
    isLoading,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    refreshPermissions,
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};